import { Metadata } from '@cognite/sdk';
import type { Comment, GroupedComments, Project, ProjectStatus } from 'types';
import { ReviewerResponse } from 'types';

export const groupComments = (comments: Comment[]): GroupedComments[] => {
  const parentsMap = new Map<string, Comment>();
  const childrenMap = new Map<string, Comment[]>();

  comments.forEach((comment) => {
    if (!comment) return;

    const meta = comment.metaData ?? {};
    const isParent = meta.isParent === 'true';

    if (isParent) {
      parentsMap.set(comment.id, comment);
    } else {
      const parentId = meta.parentCommentId;
      if (!parentId) return;

      const children = childrenMap.get(parentId) ?? [];
      children.push(comment);
      childrenMap.set(parentId, children);
    }
  });

  const groupedArray: GroupedComments[] = [];

  parentsMap.forEach((parentComment, parentId) => {
    const children = childrenMap.get(parentId) ?? [];
    children.sort((a, b) => (a?.date ?? 0) - (b?.date ?? 0));

    groupedArray.push({
      id: parentId,
      parent: parentComment,
      children,
    });
  });

  groupedArray.sort((a, b) => (a.parent?.date ?? 0) - (b.parent?.date ?? 0));

  return groupedArray;
};

export const getParentComments = (comments: Comment[]): Comment[] => {
  return comments.filter(
    (comment: Comment) => comment.metaData?.isParent === 'true'
  );
};

const isValidComment = (
  comment: Comment
): comment is Comment & {
  metaData: NonNullable<Comment['metaData']>;
} => {
  return comment.metaData != null;
};

export const getUnresolvedComments = (
  comments: Comment[],
  projectStatus: ProjectStatus
): boolean => {
  return getParentComments(comments)
    .filter(isValidComment)
    .some((comment) => {
      if (projectStatus === 'compilation') {
        return comment.metaData?.isCompilerResolved === 'false';
      }
      return comment.metaData?.reviewerResponse !== 'confirmed';
    });
};

export const buildCommentMetadata = (comment: Comment): Metadata => ({
  content: comment.commentContent,
  role: comment.role,
  createdTime: String(comment.date),
  ...comment.metaData,
});

export const checkForNeutralComments = (comments: Comment[]): boolean => {
  const parentMap = new Map<string, boolean>();

  comments.forEach((comment) => {
    const parentId = comment.metaData?.parentCommentId;
    if (parentId) {
      parentMap.set(parentId, true);
    }
  });

  const parentComments = getParentComments(comments);

  return parentComments.some((parent: Comment) => {
    const reviewerResponse = parent.metaData?.reviewerResponse;
    const parentId = parent.id;

    return reviewerResponse === 'none' && parentId && parentMap.has(parentId);
  });
};

export const isUnresolvedParentComment = (
  metaData: Comment['metaData'],
  status: ProjectStatus
): boolean => {
  if (!metaData || metaData.isParent !== 'true') {
    return false;
  }

  if (status === 'compilation') {
    return metaData.isCompilerResolved === 'false';
  }

  if (status === 'review') {
    return (
      metaData.isCompilerResolved === 'true' &&
      metaData.reviewerResponse === ReviewerResponse.None
    );
  }

  return false;
};

export const checkForLastUnresolvedComment = (
  comments: Comment[],
  groupedCommentsList: GroupedComments[],
  project: Project
): Comment[] | null => {
  if (groupedCommentsList.length === 0) {
    return null;
  }

  const unresolvedComments = comments.filter((comment) =>
    isUnresolvedParentComment(comment.metaData, project.status)
  );

  return unresolvedComments.length === 1 ? unresolvedComments : null;
};

export const shouldResetReviewerResponse = (
  parentComment: Comment,
  projectStatus: ProjectStatus
): boolean => {
  return (
    !!parentComment &&
    parentComment.metaData?.reviewerResponse === ReviewerResponse.Rejected &&
    projectStatus === 'compilation'
  );
};
